import { css } from 'twin.macro';
import { useHistory } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import AvatarUserPanel from '@/components/Features/Common/AvatarUserPanel';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

const flash = keyframes`
  0% { transform: scale(1); filter: brightness(1); }
  50% { transform: scale(1.1); filter: brightness(1.1); }
  100% { transform: scale(1); filter: brightness(1); }
`;

const avatarShortcutWrapper = css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 8px;
    cursor: pointer;
    transition: transform 0.2s ease;
`;

const badgeStyles = css`
    position: absolute;
    right: 0;
    top: 0;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: #2ecc71;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #ffffff;
    z-index: 10;
    transform: translate(25%, -25%);
`;

export const ActivityBadge = React.memo(({ count = 0, shouldFlash = false }) => {
    if (count <= 0) return null;

    return (
        <div
            css={[
                badgeStyles,
                shouldFlash &&
                    css`
                        animation: ${flash} 1.2s infinite;
                    `,
            ]}
        >
            {count > 99 ? '99+' : count}
        </div>
    );
});

const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

const Component = React.memo(({ currUser, size = 80, onClick }) => {
    const history = useHistory();
    const unreadCount = currUser?.unreadActivity || 0;
    const [shouldFlash, setShouldFlash] = useState(false);

    useEffect(() => {
        try {
            const lastVisited = localStorage.getItem('lastActivityVisitedAt');
            const isStale = !lastVisited || Date.now() - Date.parse(lastVisited) > TWENTY_FOUR_HOURS;
            setShouldFlash(unreadCount > 0 && isStale);
        } catch {
            setShouldFlash(unreadCount > 0);
        }
    }, [unreadCount]);

    const handleClick = () => {
        onClick?.() || history.push('/activity?from=map');
    };

    const avatarStyle = css`
        width: ${size}px !important;
        height: ${size}px !important;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        overflow: hidden;

        &:hover {
            box-shadow:
                rgba(0, 0, 0, 0.25) 0px 54px 55px,
                rgba(0, 0, 0, 0.12) 0px -12px 30px,
                rgba(0, 0, 0, 0.12) 0px 4px 6px,
                rgba(0, 0, 0, 0.17) 0px 12px 13px,
                rgba(0, 0, 0, 0.09) 0px -3px 5px;
        }
    `;

    const wrapperStyle = css`
        position: relative;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
    `;

    const title = 'Open Activity';

    return (
        <div css={avatarShortcutWrapper} onClick={handleClick} title={title}>
            <div css={wrapperStyle}>
                <AvatarUserPanel item={currUser} customCss={avatarStyle} roundCircleCustomCss={avatarStyle} />
                <ActivityBadge count={unreadCount} shouldFlash={shouldFlash} />
            </div>
        </div>
    );
});

const mapStateToProps = (state) => ({
    currUser: state.user.user,
});

const UserActivityAvatar = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps)(UserActivityAvatar);
